class CartUpsells extends HTMLElement {
  constructor() {
    super();
    this.contentEl = this.querySelector("[data-upsells-content]");
    this.loading = false;
    this.data = [];
    this.config = null;
    this.cartData = null;
    this.cartUpdateUnsubscriber = null;
  }

  connectedCallback() {
    this.loadConfig();
    this.loadCartData();

    if (!this.config || !this.cartData) return;

    this.init();
  }

  loadConfig() {
    try {
      const configEl = document.getElementById("UpsellConfig");
      if (!configEl) return;
      const raw = JSON.parse(configEl.textContent);
      this.config = typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (e) {
      console.error("[cart-upsells] Failed to parse config", e);
    }
  }

  loadCartData() {
    try {
      const cartEl = document.getElementById("UpsellCartData");
      if (!cartEl) return;
      this.cartData = JSON.parse(cartEl.textContent);
    } catch (e) {
      console.error("[cart-upsells] Failed to parse cart data", e);
    }
  }

  getAllowedUpsells() {
    const configUpsells = this.config?.upsells || [];
    const cartLines = this.cartData?.items || [];
    console.log({ cartLines });
    const allowed = [];

    for (const upsell of configUpsells) {
      const hasExclude = (upsell.conditions || [])
        .filter((c) => c.type === "excludes")
        .some((c) =>
          cartLines.some((line) => line.product_gid === c.variant.productId),
        );

      if (hasExclude) continue;

      let pass = true;
      for (const condition of upsell.conditions || []) {
        if (condition.type === "excludes") continue;
        const matchingLines = cartLines.filter(
          (line) => line.product_gid === condition.variant.productId,
        );
        if (condition.type === "not_contains") {
          if (matchingLines.length > 0) {
            pass = false;
            break;
          }
        } else if (matchingLines.length === 0) {
          pass = false;
          break;
        } else {
          const totalQty = matchingLines.reduce((s, l) => s + l.quantity, 0);
          const totalPrice = matchingLines.reduce(
            (s, l) => s + l.line_price,
            0,
          );
          const unitPrice = totalPrice / totalQty;

          if (condition.quantity && totalQty < Number(condition.quantity)) {
            pass = false;
            break;
          }
          if (condition.price && unitPrice < Number(condition.price)) {
            pass = false;
            break;
          }
        }
      }

      if (pass) allowed.push(upsell);
    }

    return allowed;
  }

  async init() {
    const allowed = this.getAllowedUpsells();

    if (!allowed.length) {
      this.hidden = true;
      return;
    }

    const productData = await this.fetchProducts(allowed);

    if (!productData.length) {
      this.hidden = true;
      return;
    }

    this.data = productData;
    this.hidden = false;
    this.render();
  }

  async fetchProducts(upsells) {
    const shopUrl = window.shopUrl || window.location.origin;

    const results = await Promise.all(
      upsells.map(async function (upsell) {
        try {
          const query = `
            query getProductById($id: ID!) {
              product(id: $id) {
                id
                handle
                variants(first: 100) {
                  edges {
                    node {
                      id
                      availableForSale
                    }
                  }
                }
              }
            }
          `;

          const res = await fetch(`${shopUrl}/api/2025-10/graphql.json`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              query,
              variables: { id: upsell.variant.productId },
            }),
          });

          const json = await res.json();
          const product = json?.data?.product;
          if (!product) return null;

          const variantNode = product.variants.edges
            .map((e) => e.node)
            .find(
              (v) => v.id === upsell.variant.variantId && v.availableForSale,
            );

          if (!variantNode) return null;

          return { variantNode, productNode: product, upsell };
        } catch (e) {
          console.error(
            "[cart-upsells] Failed to fetch product",
            upsell.variant.productId,
            e,
          );
        }
      }),
    );

    const productsWithGrid = await this.fetchProductGrid(
      results.filter(Boolean),
    );

    return productsWithGrid;
  }

  async fetchProductGrid(products) {
    const url = `${Shopify.routes.root}search?q=${products
      .map((p) => `${p.productNode.handle}__${p.upsell.price}`)
      .join(",")}&section_id=cart-upsells-ajax-helper`;

    try {
      const res = await fetch(url);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      return products
        .map((p) => {
          const el = doc.querySelector(
            `[data-variant-gid="${p.variantNode.id}"]`,
          );
          if (!el) return null;
          return {
            ...p,
            cardHtml: el.outerHTML,
          };
        })
        .filter(Boolean);
    } catch (e) {
      console.error("[cart-upsells] Failed to fetch product grid", e);
      return products;
    }
  }

  render() {
    const heading = this.config?.title || "";

    this.contentEl.innerHTML = `
      <h3 class="cart-upsells__heading">${this.escapeHtml(heading)}</h3>
      ${this.data
        .map((item) => {
          return item.cardHtml;
        })
        .join("")}
    `;

    this.contentEl
      .querySelectorAll(".cart-upsell-card__add-btn")
      .forEach((btn) => {
        btn.addEventListener("click", (e) => this.handleAddClick(e));
      });
  }

  async handleAddClick(e) {
    if (this.loading) return;

    const btn = e.currentTarget;
    const card = btn.closest(".cart-upsell-card");
    const variantGid = card.dataset.variantGid;

    const numericId = variantGid.split("/").pop();

    this.loading = true;
    btn.disabled = true;
    btn.textContent = "Adding...";

    try {
      await this.addToCart(numericId, 1);
    } catch (e) {
      console.error("[cart-upsells] Cart update failed", e);
      const errorEl = card.querySelector(".cart-upsell-card__error");
      if (!errorEl) {
        const err = document.createElement("div");
        err.className = "cart-upsell-card__error";
        err.textContent = "Something went wrong. Please try again.";
        card.querySelector(".cart-upsell-card__inner").appendChild(err);
      }
      btn.disabled = false;
      btn.textContent = "Add";
    }

    this.loading = false;
  }

  async addToCart(variantId) {
    this.cart =
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");

    const body = {
      id: Number(variantId),
      quantity: 1,
      properties: {
        _upsell: "true",
      },
      ...(this.cart
        ? {
            sections: this.cart.getSectionsToRender().map((s) => s.id),
            sections_url: window.location.pathname,
          }
        : {}),
    };

    if (this.cart) {
      this.cart.setActiveElement(document.activeElement);
    }

    const res = await fetch(routes.cart_add_url + ".js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("Add to cart failed");
    const response = await res.json();

    if (this.cart) {
      this.cart.renderContents(response);
    }

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: "cart-upsells",
      cartData: response,
    });
  }

  escapeHtml(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define("cart-upsells", CartUpsells);
